import type { ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { PickAllButton } from './PickAllButton';

// WHERE TWO THINGS FILL THE SAME SPACE, a row per pair — the whole product's parts as Blender
// measured them (ProductOverlaps), the covers against the parts they go on as the page measured
// them (coverCollisions). One box and one table, whoever measured.
//
// A ROW IS A SWITCH: pressed, its shared space is drawn on the model in red. The set is the
// caller's, which hands it to the viewer too. A row with nothing to draw is not a switch.
export type CollisionRow = {
  key: string;
  a: string;
  b: string;
  // How far one goes into the other, and the box of the space they share (x, y, z), millimetres.
  depth: number;
  mm: readonly number[];
  pickable: boolean;
};

const box = 'mx-auto flex w-full max-w-3xl flex-col items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3';

// CLEAR OF WHAT IS ABOVE IT, and titled INSIDE its box — the title is the box's. `call`: the
// badge that acts on what the box lists, under the title (Fix collisions, see ProductOverlaps).
export function CollisionsBox({ children, call }: { children: ReactNode; call?: ReactNode }) {
  const { t } = useLanguage();
  return (
    <section className="mt-6 w-full">
      <div className={box}>
        <h5 className="text-center text-[11px] font-bold uppercase tracking-wider text-slate-500">
          {t('notice.overlaps.title')}
        </h5>
        {call}
        {children}
      </div>
    </section>
  );
}

export function CollisionsTable({ rows, picked, onPick, onPickAll }: {
  rows: CollisionRow[];
  picked: ReadonlySet<string>;
  onPick: (key: string) => void;
  // All of these on, and nothing else — [] for none.
  onPickAll: (keys: string[]) => void;
}) {
  const { t } = useLanguage();
  const th = 'px-3 py-1.5 text-center text-xs font-semibold text-slate-600';
  const td = 'px-3 py-1.5 text-center text-xs text-slate-800';
  // EVERY ROW ON OR NONE, as the holes table has it — the rows are the picture's switches.
  const drawn = rows.filter((r) => r.pickable);
  return (
    <>
      <PickAllButton
        all={drawn.length > 0 && drawn.every((r) => picked.has(r.key))}
        count={drawn.length}
        onPick={(on) => onPickAll(on ? drawn.map((r) => r.key) : [])}
      />
      <span className="flex items-center gap-2 text-sm font-semibold text-red-600">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        {rows.length === 1 ? t('notice.overlaps.foundOne')
          : t('notice.overlaps.found').replace('{n}', String(rows.length))}
      </span>
      <table className="w-full">
        <thead>
          <tr className="border-b border-slate-300">
            <th className={th}>{t('notice.overlaps.parts')}</th>
            <th className={th}>{t('notice.overlaps.depth')}</th>
            <th className={th}>{t('notice.overlaps.shared')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          {rows.map((r) => {
            const on = r.pickable && picked.has(r.key);
            return (
              <tr
                key={r.key}
                onClick={() => r.pickable && onPick(r.key)}
                aria-pressed={on}
                className={`transition-colors ${r.pickable ? 'cursor-pointer' : ''} ${
                  on ? 'bg-red-500/15' : r.pickable ? 'hover:bg-slate-100' : ''}`}
              >
                <td className={td}>{`${r.a} ↔ ${r.b}`}</td>
                <td className={`${td} whitespace-nowrap font-semibold tabular-nums text-red-600`}>
                  {`${r.depth.toFixed(1)} mm`}
                </td>
                <td className={`${td} whitespace-nowrap tabular-nums`}>
                  {`${r.mm.map((v) => v.toFixed(1)).join(' × ')} mm`}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}
